/**
 * Drone routing, mining, and depletion — spec §7, §8 step 1.
 *
 * The phase machine is:
 *
 *   idle -> outbound -> mining -> inbound -> unloading -> idle
 *
 * Transitions happen within the tick that completes a phase, and any overshoot
 * carries into the next phase. Without the carry, a phase lasting 17 deci-ticks
 * would consume two whole ticks and throw away 3 deci-ticks at every transition,
 * so a ring-1 cycle would drift from 6.4 ticks toward 7 and throughput would
 * quietly miss the figure spec §7 derives.
 */

import {
  DECITICKS_PER_TICK,
  MINING_DECITICKS,
  UNLOAD_DECITICKS,
} from './constants';
import { apportion } from './apportion';
import { MINERAL_IDS, type MineralId } from './data';
import { hexDistanceMetres, hexKey, sortedHexKeys } from './hex';
import type { Drone, GameState, HexState } from './types';

/**
 * One-way travel time to a hex, in deci-ticks.
 *
 * speedKmPerTick * 100 converts km-per-tick into metres-per-deci-tick.
 */
export function travelDeciTicks(distanceMetres: number, speedKmPerTick: number): number {
  return Math.round(distanceMetres / (speedKmPerTick * 100));
}

/** Full round-trip cycle length in deci-ticks. Used by tests and the UI. */
export function cycleDeciTicks(distanceMetres: number, speedKmPerTick: number): number {
  return (
    travelDeciTicks(distanceMetres, speedKmPerTick) * 2 + MINING_DECITICKS + UNLOAD_DECITICKS
  );
}

/** Total units left in a hex, across all minerals. */
export function totalDeposits(deposits: Record<MineralId, number>): number {
  let total = 0;
  for (const id of MINERAL_IDS) total += deposits[id];
  return total;
}

function emptyCargo(): Record<MineralId, number> {
  const out = {} as Record<MineralId, number>;
  for (const id of MINERAL_IDS) out[id] = 0;
  return out;
}

/**
 * Strip mining — spec deviation E13.
 *
 * A drone digs a hole and takes whatever the ground holds, in the proportions it
 * holds it. It does not sort ore at the rock face. So one trip returns a mix
 * matching the hex's current composition, and which hex you send a drone to IS
 * the decision about what you get.
 *
 * The split goes through the shared apportionment helper, which is also what
 * dissociation uses — proportional division is easy to get subtly wrong, and
 * having exactly one implementation is how it stays right in both places.
 *
 * Returns amounts summing to exactly `min(capacity, total)`.
 */
export function stripMine(
  deposits: Record<MineralId, number>,
  capacity: number,
): Record<MineralId, number> {
  const total = totalDeposits(deposits);
  if (total <= 0 || capacity <= 0) return emptyCargo();

  return apportion(
    MINERAL_IDS,
    (id) => deposits[id],
    Math.min(capacity, total),
    (id) => deposits[id],
  );
}

/** Does this hex still hold anything at all? */
function hexHasWork(hex: HexState): boolean {
  return totalDeposits(hex.deposits) > 0;
}

function assignedHexOf(state: GameState, drone: Drone): HexState | null {
  if (drone.assignedHex === null) return null;
  return state.planet.hexes[hexKey(drone.assignedHex)] ?? null;
}

function notify(
  state: GameState,
  kind: 'hexDepleted' | 'droneIdle',
  message: string,
): void {
  state.notifications.push({
    id: state.nextNotificationId,
    tick: state.tick,
    kind,
    message,
  });
  state.nextNotificationId += 1;
}

/**
 * Send an idle drone out, if it has somewhere to go. Returns the phase duration
 * in deci-ticks, or null if the drone stays idle.
 */
function beginOutbound(state: GameState, drone: Drone): number | null {
  let hex = assignedHexOf(state, drone);

  if (hex === null || !hexHasWork(hex)) {
    // AUTO-ROUTE FINDS THE FIRST JOB TOO, not only the next one. This was
    // consulted solely when a hex ran dry mid-run, so switching it on for an
    // idle drone with no assignment did nothing whatsoever — and every drone
    // is idle and unassigned when a game starts, which is exactly when a
    // player reaches for the button.
    if (!drone.autoReassign) return null;
    const target = findRichestHex(state);
    if (target === null) return null;
    drone.assignedHex = { q: target.coord.q, r: target.coord.r };
    hex = target;
  }

  const distance = hexDistanceMetres({ q: 0, r: 0 }, hex.coord);
  return travelDeciTicks(distance, drone.speedKmPerTick);
}

/**
 * Advance one drone by one tick.
 *
 * Structured as: spend the tick, then resolve every phase boundary the spend
 * crossed. The loop is bounded by the number of phases, not by data, so it
 * cannot spin.
 */
function advanceDrone(state: GameState, drone: Drone): void {
  if (drone.phase === 'idle') {
    const duration = beginOutbound(state, drone);
    if (duration === null) return;
    drone.phase = 'outbound';
    drone.phaseDeciTicksRemaining = duration;
  }

  drone.phaseDeciTicksRemaining -= DECITICKS_PER_TICK;

  // At most a handful of transitions can complete in one tick even with short
  // phases; the bound is a safety net, not an expected limit.
  for (let guard = 0; guard < 8 && drone.phaseDeciTicksRemaining <= 0; guard++) {
    const overshoot = drone.phaseDeciTicksRemaining; // <= 0, carried forward

    switch (drone.phase) {
      case 'outbound': {
        drone.phase = 'mining';
        drone.phaseDeciTicksRemaining = MINING_DECITICKS + overshoot;
        break;
      }

      case 'mining': {
        const hex = assignedHexOf(state, drone);

        if (hex === null || !hexHasWork(hex)) {
          // The hex ran dry while this drone was inbound to it. Go home empty.
          drone.cargo = null;
        } else {
          const taken = stripMine(hex.deposits, drone.capacity);
          for (const id of MINERAL_IDS) hex.deposits[id] -= taken[id];
          drone.cargo = taken;

          if (!hexHasWork(hex)) {
            notify(state, 'hexDepleted', `Hex ${hexKey(hex.coord)} is exhausted.`);
          }
        }

        const distance =
          hex === null ? 0 : hexDistanceMetres({ q: 0, r: 0 }, hex.coord);
        drone.phase = 'inbound';
        drone.phaseDeciTicksRemaining =
          travelDeciTicks(distance, drone.speedKmPerTick) + overshoot;
        break;
      }

      case 'inbound': {
        drone.phase = 'unloading';
        drone.phaseDeciTicksRemaining = UNLOAD_DECITICKS + overshoot;
        break;
      }

      case 'unloading': {
        if (drone.cargo !== null) {
          for (const id of MINERAL_IDS) {
            state.inventory.minerals[id] += drone.cargo[id];
          }
          drone.cargo = null;
        }

        const hex = assignedHexOf(state, drone);
        const stillWorkable = hex !== null && hexHasWork(hex);

        if (stillWorkable) {
          const distance = hexDistanceMetres({ q: 0, r: 0 }, hex.coord);
          drone.phase = 'outbound';
          drone.phaseDeciTicksRemaining =
            travelDeciTicks(distance, drone.speedKmPerTick) + overshoot;
        } else {
          // Spec §13 item 3: default is manual, so a depleted hex surfaces as
          // an event the player has to answer rather than being routed around.
          if (drone.autoReassign) {
            const target = findRichestHex(state);
            if (target !== null) {
              drone.assignedHex = { q: target.coord.q, r: target.coord.r };
              const distance = hexDistanceMetres({ q: 0, r: 0 }, target.coord);
              drone.phase = 'outbound';
              drone.phaseDeciTicksRemaining =
                travelDeciTicks(distance, drone.speedKmPerTick) + overshoot;
              break;
            }
          }

          drone.phase = 'idle';
          drone.phaseDeciTicksRemaining = 0;
          notify(state, 'droneIdle', `Drone ${drone.id} is idle: nothing left to mine.`);
        }
        break;
      }
    }

    // 'idle' is not a case above: the guard at the top of advanceDrone either
    // launched the drone or returned, so an idle drone never reaches the loop.
    // It can become idle here, though, when a hex runs dry.
    if (drone.phase === 'idle') break;
  }
}

/**
 * Richest remaining hex, for auto-reassignment. Ranked by total material left,
 * since a strip-mining drone takes everything rather than one mineral. Iterates
 * in sorted key order so ties resolve identically on every machine.
 */
function findRichestHex(state: GameState): HexState | null {
  let best: HexState | null = null;
  let bestAmount = 0;

  for (const key of sortedHexKeys(state.planet.hexes)) {
    const hex = state.planet.hexes[key]!;
    const amount = totalDeposits(hex.deposits);
    if (amount > bestAmount) {
      bestAmount = amount;
      best = hex;
    }
  }

  return best;
}

/** Spec §8 step 1. Drones advance in id order (spec §11 rule 3). */
export function advanceDrones(state: GameState): void {
  const ordered = [...state.drones].sort((a, b) => a.id - b.id);
  for (const drone of ordered) advanceDrone(state, drone);
}
